import * as dbDocumentations from '../db/documentations/documentations';
import * as dbIssues from '../db/issues/issues';
import * as dbLevels from '../db/levels/levels';
import * as dbSites from '../db/sites/sites';
import * as dbDirectories from 'serviceWorker/db/directories/directories';
import { DocumentationModel, DocumentationSourceIssue } from 'shared/domain/documentation/documentationModel';
import { DirectoryModel } from 'shared/domain/directory/directoryModel';
import { documentationEntityToModel } from 'serviceWorker/repository/documentation/mappings';
import { createDocumentationIssuesMap } from 'serviceWorker/repository/documentation/createDocumentationIssuesMap';
import { createDocumentationSourceIssueMap } from 'serviceWorker/repository/documentation/createDocumentationSourceIssueMap';
import { createDocumentationSitesAndLevelsMap } from 'serviceWorker/repository/documentation/createDocumentationSitesAndLevelsMap';
import { DocumentationEntity } from 'serviceWorker/repository/documentation/entity';
import { UserEntity } from 'shared/domain/user/types/entity';
import { SiteEntity } from 'shared/domain/site/types/entity';
import { LevelEntity } from 'shared/domain/level/types/entity';
import { isSuccessUpload } from 'shared/domain/documentation/documentationVersionHelpers';
import { hasValidId } from 'shared/utils/id/entityIds';
import { getUsersSynchronized } from 'serviceWorker/services/users/getUsers';
import { directoryEntityToModel } from 'serviceWorker/repository/directory/mappings';

interface DocumentationRelatedData {
  docs: DocumentationEntity[];
  users: UserEntity[];
  docIssuesMap: Map<string, number>;
  docSourceIssueMap: Map<string, DocumentationSourceIssue>;
  docSitesMap: Map<string, SiteEntity[]>;
  docLevelsMap: Map<string, LevelEntity[]>;
}

function toValidDocumentationModels(
  docs: DocumentationEntity[],
  users: UserEntity[],
  docIssuesMap: Map<string, number>,
  docSourceIssueMap: Map<string, DocumentationSourceIssue>,
  docSitesMap: Map<string, SiteEntity[]>,
  docLevelsMap: Map<string, LevelEntity[]>
): DocumentationModel[] {
  return docs
    .map((doc) =>
      documentationEntityToModel(
        doc,
        users,
        docIssuesMap,
        docSourceIssueMap,
        docSitesMap,
        docLevelsMap
      )
    )
    .filter(
      (documentation) =>
        hasValidId(documentation) && isSuccessUpload(documentation)
    );
}

export async function getDirectoriesByQuery(
  value: string
): Promise<DirectoryModel[]> {
  const [directories, users] = await Promise.all([
    dbDirectories.getByQuery({ parentId: value }),
    getUsersSynchronized(),
  ]);

  return directories.map((directory) => directoryEntityToModel(directory, users));
}

export async function getDocumentationDataWithRelatedData(
  value: string
): Promise<DocumentationRelatedData> {
  const [docs, users, issues, sites, levels] = await Promise.all([
    dbDocumentations.getByQuery({ parentId: value }),
    getUsersSynchronized(),
    dbIssues.getAll(),
    dbSites.get(),
    dbLevels.get(),
  ]);

  const docIssuesMap = createDocumentationIssuesMap(issues, levels);
  const docSourceIssueMap = createDocumentationSourceIssueMap(issues);
  const { docSitesMap, docLevelsMap } =
    createDocumentationSitesAndLevelsMap(sites, levels);

  return {
    docs,
    users,
    docIssuesMap,
    docSourceIssueMap,
    docSitesMap,
    docLevelsMap,
  };
}

export async function getDocumentationsByQuery(
  value: string
): Promise<DocumentationModel[]> {
  const {
    docs,
    users,
    docIssuesMap,
    docSourceIssueMap,
    docSitesMap,
    docLevelsMap,
  } = await getDocumentationDataWithRelatedData(value);

  return toValidDocumentationModels(
    docs,
    users,
    docIssuesMap,
    docSourceIssueMap,
    docSitesMap,
    docLevelsMap
  );
}

export async function getSearchedDocumentations(
  value: string
): Promise<DocumentationModel[]> {
  const docs = await dbDocumentations.getSearched(value);

  const [users, issues, sites, levels] = await Promise.all([
    getUsersSynchronized(),
    dbIssues.getAll(),
    dbSites.get(),
    dbLevels.get(),
  ]);

  const docIssuesMap = createDocumentationIssuesMap(issues, levels);
  const docSourceIssueMap = createDocumentationSourceIssueMap(issues);
  const { docSitesMap, docLevelsMap } =
    createDocumentationSitesAndLevelsMap(sites, levels);

  return toValidDocumentationModels(
    docs,
    users,
    docIssuesMap,
    docSourceIssueMap,
    docSitesMap,
    docLevelsMap
  );
}

export async function getAllDocumentationModels(): Promise<DocumentationModel[]> {
  const docs = await dbDocumentations.get();

  const [users, issues, sites, levels] = await Promise.all([
    getUsersSynchronized(),
    dbIssues.getAll(),
    dbSites.get(),
    dbLevels.get(),
  ]);

  const docIssuesMap = createDocumentationIssuesMap(issues, levels);
  const docSourceIssueMap = createDocumentationSourceIssueMap(issues);
  const { docSitesMap, docLevelsMap } =
    createDocumentationSitesAndLevelsMap(sites, levels);

  return toValidDocumentationModels(
    docs,
    users,
    docIssuesMap,
    docSourceIssueMap,
    docSitesMap,
    docLevelsMap
  );
}
